import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Monitor,
  Search,
  Filter,
  MoreVertical,
  Wifi,
  WifiOff,
  RefreshCw,
  Trash2,
  Edit2,
  Play,
  Copy,
  ShieldCheck,
  KeyRound,
  ExternalLink,
  Rocket,
} from 'lucide-react'
import { cn, formatRelativeTime, getStatusColor, getStatusBgColor } from '@/lib/utils'
import api from '@/lib/api'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import { useSearchParams } from 'react-router-dom'

interface Device {
  id: string
  deviceId: string
  name: string
  status: 'online' | 'offline' | 'busy'
  claimStatus: 'pending' | 'claimed' | 'revoked'
  customerId: string
  customerName: string
  os: string | null
  lastSeen: string | null
  version: string | null
  ipAddress: string | null
  rustdeskId: string | null
  unattendedEnabled: boolean
  ownerUserName: string | null
}

interface EnrollmentToken {
  id: string
  name: string
  organizationId: string
  organizationName: string
  maxUses: number
  usedCount: number
  expiresAt: string | null
  revokedAt: string | null
  lastUsedAt: string | null
  createdAt: string
  isActive: boolean
  plainTextToken?: string
  claimUrl?: string
}

interface Customer {
  id: string
  name: string
}

interface SessionLaunchResponse {
  id: string
  deviceName: string
  status: 'active'
  startedAt: string
  connection: {
    method: 'rustdesk'
    launchUrl: string
    ticket: string
    expiresAt: string
    relay: {
      rendezvousServer: string
      relayPort: number
      apiPort: number
      publicKeyConfigured: boolean
    }
  }
}

const placeholderDevices: Device[] = [
  {
    id: '1',
    deviceId: 'SC-4821-7593',
    name: 'Sim Rig #1',
    status: 'online',
    claimStatus: 'claimed',
    customerId: '1',
    customerName: 'Speed Racing Center',
    os: 'Windows 11 Pro',
    lastSeen: new Date().toISOString(),
    version: '1.2.3',
    ipAddress: '192.168.1.105',
    rustdeskId: '1389475621',
    unattendedEnabled: true,
    ownerUserName: 'Tom Racing',
  },
  {
    id: '2',
    deviceId: 'SC-8294-1057',
    name: 'Reception PC',
    status: 'busy',
    claimStatus: 'claimed',
    customerId: '2',
    customerName: 'Downtown Sim Lounge',
    os: 'Windows 10 Pro',
    lastSeen: new Date().toISOString(),
    version: '1.2.3',
    ipAddress: '10.0.0.25',
    rustdeskId: '2275148971',
    unattendedEnabled: true,
    ownerUserName: 'Emily Chen',
  },
  {
    id: '3',
    deviceId: 'SC-2847-9163',
    name: 'Training Station',
    status: 'offline',
    claimStatus: 'pending',
    customerId: '1',
    customerName: 'Speed Racing Center',
    os: 'Windows 10 Pro',
    lastSeen: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    version: '1.1.0',
    ipAddress: null,
    rustdeskId: null,
    unattendedEnabled: false,
    ownerUserName: 'Tom Racing',
  },
]

export default function DevicesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [claimFilter, setClaimFilter] = useState<string>('all')
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null)
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [tokenName, setTokenName] = useState('Field Install Token')
  const [maxUses, setMaxUses] = useState('5')
  const [expiresInDays, setExpiresInDays] = useState('30')
  const [latestToken, setLatestToken] = useState<EnrollmentToken | null>(null)
  const [launchSession, setLaunchSession] = useState<SessionLaunchResponse | null>(null)

  const queryClient = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)
  const canManageEnrollment = currentUser?.role === 'admin' || currentUser?.role === 'technician'
  const customerFilter = searchParams.get('customerId') || 'all'

  useEffect(() => {
    const customerId = searchParams.get('customerId')
    if (customerId) {
      setSearch('')
    }
  }, [searchParams])

  const { data: devices, refetch } = useQuery<Device[]>({
    queryKey: ['devices', statusFilter, claimFilter],
    queryFn: async () => {
      const response = await api.get('/devices', {
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          claimStatus: claimFilter !== 'all' ? claimFilter : undefined,
        },
      })
      return response.data
    },
    placeholderData: placeholderDevices,
  })

  const { data: customers } = useQuery<Customer[]>({
    queryKey: ['customers-for-devices'],
    queryFn: async () => {
      const response = await api.get('/customers')
      return response.data.map((customer: { id: string; name: string }) => ({
        id: customer.id,
        name: customer.name,
      }))
    },
    enabled: canManageEnrollment,
    placeholderData: [
      { id: '1', name: 'Speed Racing Center' },
      { id: '2', name: 'Downtown Sim Lounge' },
    ],
  })

  const { data: enrollmentTokens } = useQuery<EnrollmentToken[]>({
    queryKey: ['enrollment-tokens'],
    queryFn: async () => {
      const response = await api.get('/device-enrollment/tokens')
      return response.data
    },
    enabled: canManageEnrollment,
    placeholderData: [],
  })

  const createTokenMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/device-enrollment/tokens', {
        organizationId: selectedOrgId,
        name: tokenName,
        maxUses: Number(maxUses),
        expiresInDays: Number(expiresInDays),
      })
      return response.data as EnrollmentToken
    },
    onSuccess: (data) => {
      setLatestToken(data)
      setShowEnrollmentForm(false)
      queryClient.invalidateQueries({ queryKey: ['enrollment-tokens'] })
      toast.success('Enrollment token created')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create enrollment token')
    },
  })

  const startSessionMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      const response = await api.post('/sessions', { deviceId })
      return response.data as SessionLaunchResponse
    },
    onSuccess: (data) => {
      setLaunchSession(data)
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      toast.success(`Session started for ${data.deviceName}`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start session')
    },
  })

  const updateDeviceMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await api.patch(`/devices/${id}`, payload)
      return response.data as Device
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('Device updated')
      setSelectedDevice(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update device')
    },
  })

  const deleteDeviceMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/devices/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      toast.success('Device deleted')
      setSelectedDevice(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete device')
    },
  })

  const filteredDevices = devices?.filter((device) => {
    if (customerFilter !== 'all' && device.customerId !== customerFilter) {
      return false
    }

    if (!search) {
      return true
    }

    const searchLower = search.toLowerCase()
    return (
      device.name.toLowerCase().includes(searchLower) ||
      device.deviceId.toLowerCase().includes(searchLower) ||
      device.customerName.toLowerCase().includes(searchLower) ||
      (device.rustdeskId || '').toLowerCase().includes(searchLower)
    )
  })

  const copyToClipboard = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value)
    toast.success(`${label} copied to clipboard`)
  }

  const handleEditDevice = (device: Device) => {
    const name = window.prompt('Device name', device.name)?.trim()
    if (!name) return

    const rustdeskId = window.prompt('RustDesk ID', device.rustdeskId || '')?.trim() || null
    const statusInput = window.prompt('Status: online, offline, busy', device.status)?.trim().toLowerCase()
    const claimInput = window.prompt('Claim status: claimed, pending, revoked', device.claimStatus)?.trim().toLowerCase()

    const status = statusInput === 'offline' ? 'OFFLINE' : statusInput === 'busy' ? 'BUSY' : 'ONLINE'
    const claimStatus = claimInput === 'pending' ? 'PENDING' : claimInput === 'revoked' ? 'REVOKED' : 'CLAIMED'

    updateDeviceMutation.mutate({
      id: device.id,
      payload: {
        name,
        rustdeskId,
        status,
        claimStatus,
        unattendedEnabled: device.unattendedEnabled,
      },
    })
  }

  const handleDeleteDevice = (device: Device) => {
    if (!window.confirm(`Delete ${device.name}? This cannot be undone.`)) {
      return
    }

    deleteDeviceMutation.mutate(device.id)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <Wifi className="w-4 h-4 text-sc-accent-green" />
      case 'offline':
        return <WifiOff className="w-4 h-4 text-sc-text-muted" />
      case 'busy':
        return <Wifi className="w-4 h-4 text-sc-accent-orange" />
      default:
        return null
    }
  }

  const getClaimBadge = (claimStatus: Device['claimStatus']) => {
    switch (claimStatus) {
      case 'claimed':
        return (
          <span className="badge bg-sc-primary/10 text-sc-primary">
            <ShieldCheck className="w-3 h-3" />
            Claimed
          </span>
        )
      case 'pending':
        return (
          <span className="badge bg-sc-accent-yellow/10 text-sc-accent-yellow">
            Pending
          </span>
        )
      case 'revoked':
        return (
          <span className="badge bg-sc-accent-red/10 text-sc-accent-red">
            Revoked
          </span>
        )
      default:
        return null
    }
  }

  const openLaunchUrl = () => {
    if (!launchSession) {
      return
    }

    try {
      window.location.href = launchSession.connection.launchUrl
      window.setTimeout(() => {
        toast('If the technician app did not open, copy the launch URL or install the launcher first.')
      }, 1200)
    } catch {
      toast.error('Failed to open technician app')
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sc-text-primary">Devices</h1>
          <p className="text-sc-text-muted mt-1">
            Claim, launch, and manage account-linked remote devices
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => refetch()} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {canManageEnrollment && (
            <button
              onClick={() => setShowEnrollmentForm((current) => !current)}
              className="btn-primary"
            >
              <KeyRound className="w-4 h-4" />
              {showEnrollmentForm ? 'Hide Enrollment' : 'Create Enrollment Token'}
            </button>
          )}
        </div>
      </div>

      {canManageEnrollment && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-sc-text-primary">Enrollment Tokens</h2>
                <p className="text-sm text-sc-text-muted mt-1">
                  Issue secure claim tokens that attach PCs to customer accounts
                </p>
              </div>
              <span className="badge bg-sc-bg-light text-sc-text-secondary">
                {enrollmentTokens?.length || 0} active records
              </span>
            </div>
            <div className="card-body space-y-4">
              {showEnrollmentForm && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl bg-sc-bg-medium/40 border border-sc-border-dark">
                  <div className="md:col-span-2">
                    <label className="block text-sm text-sc-text-secondary mb-2">Organization</label>
                    <select
                      value={selectedOrgId}
                      onChange={(event) => setSelectedOrgId(event.target.value)}
                      className="input"
                    >
                      <option value="">Choose customer</option>
                      {customers?.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-sc-text-secondary mb-2">Token name</label>
                    <input
                      value={tokenName}
                      onChange={(event) => setTokenName(event.target.value)}
                      className="input"
                      placeholder="Front desk deployment batch"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-sc-text-secondary mb-2">Max uses</label>
                    <input
                      value={maxUses}
                      onChange={(event) => setMaxUses(event.target.value)}
                      className="input"
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-sc-text-secondary mb-2">Expires (days)</label>
                    <input
                      value={expiresInDays}
                      onChange={(event) => setExpiresInDays(event.target.value)}
                      className="input"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <button
                      onClick={() => createTokenMutation.mutate()}
                      disabled={!selectedOrgId || !tokenName || createTokenMutation.isPending}
                      className="btn-primary w-full"
                    >
                      <Rocket className="w-4 h-4" />
                      {createTokenMutation.isPending ? 'Creating...' : 'Create Token'}
                    </button>
                  </div>
                </div>
              )}

              {latestToken && latestToken.plainTextToken && (
                <div className="p-4 rounded-xl border border-sc-primary/30 bg-sc-primary/5 space-y-3 animate-in">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium text-sc-text-primary">Newest token</p>
                      <p className="text-sm text-sc-text-muted">{latestToken.organizationName}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(latestToken.plainTextToken!, 'Enrollment token')}
                      className="btn-secondary py-1.5 px-3 text-xs"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy token
                    </button>
                  </div>
                  <code className="block p-3 rounded-lg bg-sc-bg-dark text-sc-text-primary break-all text-sm">
                    {latestToken.plainTextToken}
                  </code>
                  {latestToken.claimUrl && (
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => copyToClipboard(latestToken.claimUrl!, 'Claim URL')}
                        className="btn-secondary py-1.5 px-3 text-xs"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Copy claim URL
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {(enrollmentTokens || []).slice(0, 5).map((token) => (
                  <div
                    key={token.id}
                    className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 rounded-xl bg-sc-bg-medium/30 border border-sc-border-dark"
                  >
                    <div>
                      <p className="font-medium text-sc-text-primary">{token.name}</p>
                      <p className="text-sm text-sc-text-muted">
                        {token.organizationName} • {token.usedCount}/{token.maxUses} uses
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'badge',
                        token.isActive ? 'badge-online' : 'badge-offline'
                      )}>
                        {token.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-xs text-sc-text-muted">
                        {token.expiresAt ? `Expires ${formatRelativeTime(token.expiresAt)}` : 'No expiry'}
                      </span>
                    </div>
                  </div>
                ))}
                {(!enrollmentTokens || enrollmentTokens.length === 0) && (
                  <p className="text-sm text-sc-text-muted">
                    No enrollment tokens yet. Create one to securely bind installs to a customer account.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-sc-text-primary">Technician Launch</h2>
              <p className="text-sm text-sc-text-muted mt-1">
                Start a session and hand it off to the local technician app
              </p>
            </div>
            <div className="card-body">
              {launchSession ? (
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-sc-text-primary">{launchSession.deviceName}</p>
                    <p className="text-sm text-sc-text-muted">
                      Ticket expires {formatRelativeTime(launchSession.connection.expiresAt)}
                    </p>
                  </div>
                  <code className="block p-3 rounded-lg bg-sc-bg-medium text-sm break-all">
                    {launchSession.connection.ticket}
                  </code>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-sc-bg-medium/50">
                      <p className="text-sc-text-muted">Relay</p>
                      <p className="text-sc-text-primary mt-1">
                        {launchSession.connection.relay.rendezvousServer}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-sc-bg-medium/50">
                      <p className="text-sc-text-muted">Ports</p>
                      <p className="text-sc-text-primary mt-1">
                        {launchSession.connection.relay.relayPort} / {launchSession.connection.relay.apiPort}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={openLaunchUrl} className="btn-primary">
                      <Play className="w-4 h-4" />
                      Launch Technician App
                    </button>
                    <button
                      onClick={() => copyToClipboard(launchSession.connection.launchUrl, 'Launch URL')}
                      className="btn-secondary"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Copy Launch URL
                    </button>
                    <button
                      onClick={() => copyToClipboard(launchSession.connection.ticket, 'Connection ticket')}
                      className="btn-secondary"
                    >
                      <Copy className="w-4 h-4" />
                      Copy Ticket
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-sc-text-muted">
                  Start a session from the device table. We’ll generate a short-lived connection
                  ticket and a launch URL for the local support app.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sc-text-muted" />
              <input
                type="text"
                placeholder="Search devices, customers, or RustDesk IDs..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-sc-text-muted" />
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="input w-auto"
              >
                <option value="all">All Status</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="busy">In Session</option>
              </select>
            </div>

            <select
              value={customerFilter}
              onChange={(event) => {
                const next = event.target.value
                if (next === 'all') {
                  setSearchParams({})
                } else {
                  setSearchParams({ customerId: next })
                }
              }}
              className="input w-auto"
            >
              <option value="all">All Customers</option>
              {(customers || []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>

            <select
              value={claimFilter}
              onChange={(event) => setClaimFilter(event.target.value)}
              className="input w-auto"
            >
              <option value="all">All Claim States</option>
              <option value="claimed">Claimed</option>
              <option value="pending">Pending</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Customer</th>
                <th>Claim</th>
                <th>RustDesk ID</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices?.map((device) => {
                const canConnect =
                  device.status !== 'offline' &&
                  device.claimStatus === 'claimed' &&
                  Boolean(device.rustdeskId)

                return (
                  <tr key={device.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center',
                          getStatusBgColor(device.status)
                        )}>
                          <Monitor className={cn('w-5 h-5', getStatusColor(device.status))} />
                        </div>
                        <div>
                          <p className="font-medium text-sc-text-primary">{device.name}</p>
                          <p className="text-xs text-sc-text-muted">
                            {device.deviceId}
                            {device.ownerUserName ? ` • ${device.ownerUserName}` : ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-sc-text-secondary">{device.customerName}</td>
                    <td>{getClaimBadge(device.claimStatus)}</td>
                    <td>
                      {device.rustdeskId ? (
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono text-sc-text-secondary bg-sc-bg-medium px-2 py-1 rounded">
                            {device.rustdeskId}
                          </code>
                          <button
                            onClick={() => copyToClipboard(device.rustdeskId!, 'RustDesk ID')}
                            className="p-1 text-sc-text-muted hover:text-sc-text-primary"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-sc-text-muted">Not enrolled yet</span>
                      )}
                    </td>
                    <td>
                      <span className={cn(
                        'badge',
                        device.status === 'online' && 'badge-online',
                        device.status === 'offline' && 'badge-offline',
                        device.status === 'busy' && 'badge-busy'
                      )}>
                        {getStatusIcon(device.status)}
                        {device.status === 'busy'
                          ? 'In Session'
                          : device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                      </span>
                    </td>
                    <td className="text-sc-text-muted text-sm">
                      {device.lastSeen ? formatRelativeTime(device.lastSeen) : 'Never'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startSessionMutation.mutate(device.id)}
                          disabled={!canConnect || startSessionMutation.isPending}
                          className={cn(
                            'btn-primary py-1.5 px-3 text-xs',
                            !canConnect && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          <Play className="w-3 h-3" />
                          {startSessionMutation.isPending ? 'Starting...' : 'Connect'}
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setSelectedDevice(selectedDevice === device.id ? null : device.id)}
                            className="p-2 text-sc-text-muted hover:text-sc-text-primary hover:bg-sc-bg-light rounded-lg"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {selectedDevice === device.id && (
                            <div className="absolute right-0 top-full mt-1 w-44 bg-sc-bg-medium border border-sc-border-medium rounded-lg shadow-sc-lg z-10 py-1">
                              <button
                                onClick={() => handleEditDevice(device)}
                                className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => device.rustdeskId && copyToClipboard(device.rustdeskId, 'RustDesk ID')}
                                className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                              >
                                <Copy className="w-4 h-4" />
                                Copy Host ID
                              </button>
                              <button
                                onClick={() => handleDeleteDevice(device)}
                                className="w-full px-3 py-2 text-left text-sm text-sc-accent-red hover:bg-sc-bg-light flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredDevices?.length === 0 && (
          <div className="p-12 text-center">
            <Monitor className="w-12 h-12 text-sc-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-sc-text-primary mb-2">
              No devices found
            </h3>
            <p className="text-sc-text-muted mb-4">
              {search ? 'Try adjusting your search terms' : 'Create an enrollment token and claim your first device'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
